import { buildLoadedAPagePayload, type LoadedAPagePayload } from "@/utils/buildLoadedAPagePayload";

/**
 * Map Loaded-a-Page fields → gtag event params (omit null/undefined).
 */
export function toGtagPageViewParams(payload: LoadedAPagePayload): Record<string, string | number> {
  const raw: Record<string, string | number | null | undefined> = {
    page_title: payload.title,
    page_location: payload.url,
    page_path: payload.path,
    page_referrer: payload.page_referrer,
    previous_url: payload.page_referrer,
    business_line: payload.business_line,
    page_type: payload.page_type,
    product_line: payload.product_line,
    product_gender: payload.product_gender,
    page_detail: payload.page_detail,
    page_template: payload.page_template,
    page_intent: payload.page_intent,
    marketing_owner: payload.marketing_owner,
    marketing_type: payload.marketing_type,
    nutrafolBrandTag: payload.nutrafolBrandTag,
    user_agent_string: payload.userAgent,
    name: payload.name,
    segment_anonymous_id: payload.segmentAnonymousId,
    OnetrustActiveGroups: payload.OnetrustActiveGroups,
    OptanonActiveGroups: payload.OptanonActiveGroups,
    page_view_count: 1,
  };

  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value !== null && value !== undefined && value !== "") {
      out[key] = value;
    }
  }
  return out;
}

declare global {
  interface Window {
    __nutrafolSendGa4ColdPageView?: () => void;
    __nutrafolGa4ColdPageViewSent?: boolean;
  }
}

let coldPageViewRetryId: number | null = null;

/**
 * https://nutrafol.atlassian.net/browse/NT2-30510
 * Fallback cold-load page_view (OptanonWrapper). Primary send is in gtag config snippet.
 */
export function sendGa4PageView(): void {
  if (typeof window === "undefined") return;
  if (window.__nutrafolGa4ColdPageViewSent) return;

  const tagId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;
  if (!tagId) return;

  const fire = () => {
    if (window.__nutrafolGa4ColdPageViewSent) return true;
    if (typeof window.gtag !== "function") return false;

    const params = toGtagPageViewParams(buildLoadedAPagePayload());
    window.gtag("event", "page_view", {
      send_to: tagId,
      ...params,
    });
    window.__nutrafolGa4ColdPageViewSent = true;
    if (coldPageViewRetryId !== null) {
      window.clearInterval(coldPageViewRetryId);
      coldPageViewRetryId = null;
    }
    return true;
  };

  if (fire()) return;

  if (coldPageViewRetryId !== null) return;

  let attempts = 0;
  coldPageViewRetryId = window.setInterval(() => {
    attempts += 1;
    if (fire() || attempts >= 15) {
      if (coldPageViewRetryId !== null) {
        window.clearInterval(coldPageViewRetryId);
        coldPageViewRetryId = null;
      }
    }
  }, 100);
}

/** Register cold-load sender for OptanonWrapper (no-op if head already sent). */
export function registerGa4ColdPageViewHook(): void {
  if (typeof window === "undefined") return;
  window.__nutrafolSendGa4ColdPageView = () => sendGa4PageView();
}
